import React, { useEffect, useState } from 'react';
import { sitesAPI } from '../services/api';
import { formatDateBritish } from '../utils/dateUtils';
import PostcodeLookupInput from '../components/PostcodeLookupInput';
import './VehicleList.css';
import additionIcon from '../assets/icons/addition.png';

interface Site {
  id: number;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  county?: string;
  postcode: string;
  description: string;
  clientId: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const SiteList: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postcode: '',
    description: '',
    clientId: 0,
  });

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const response = await sitesAPI.getAll();
      // Filter out inactive (deleted) sites from display
      const activeSites = response.data.filter((site: Site) => site.isActive !== false);
      setSites(activeSites);
    } catch (error) {
      console.error('Error loading sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingSite) {
        const updateData = {
          ...editingSite,
          ...formData,
        };
        await sitesAPI.update(editingSite.id, updateData);
      } else {
        await sitesAPI.create(formData);
      }
      
      loadSites();
      resetForm();
    } catch (error) {
      console.error('Error saving site:', error);
      alert('Failed to save site');
    }
  };

  const handleEdit = (site: Site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      addressLine1: site.addressLine1,
      addressLine2: site.addressLine2 || '',
      city: site.city,
      county: site.county || '',
      postcode: site.postcode,
      description: site.description,
      clientId: site.clientId,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this site? It will be removed from view but kept in the system.')) {
      return;
    }

    try {
      await sitesAPI.delete(id);
      // Remove from local state immediately for better UX
      setSites(sites.filter(s => s.id !== id));
    } catch (error) {
      console.error('Error deleting site:', error);
      alert('Failed to delete site');
      loadSites(); // Reload if there was an error
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postcode: '',
      description: '',
      clientId: 0,
    });
    setEditingSite(null);
    setShowForm(false);
  };

  if (loading) {
    return <div>Loading sites...</div>;
  }

  return (
    <div className="vehicle-list">
      <div className="page-header">
        <h1>Sites & Locations</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)} style={{ display: 'flex', alignItems: 'center' }}>
          <img src={additionIcon} alt="" style={{width: '16px', height: '16px', marginRight: '8px'}} />
          Add Site
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSite ? 'Edit Site' : 'Add New Site'}</h2>
              <button className="close-btn" onClick={resetForm}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Site Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Main Office, Warehouse A"
                />
              </div>

              {/* Postcode lookup — populates all address fields */}
              {!editingSite && (
                <div style={{ padding: '12px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, marginBottom: 16 }}>
                  <label style={{ fontWeight: 700, fontSize: 13, color: '#0c4a6e', display: 'block', marginBottom: 8 }}>🔍 Search by Postcode</label>
                  <PostcodeLookupInput
                    onSelect={a => setFormData(prev => ({
                      ...prev,
                      // Use organisation as site name if the user hasn't typed one yet
                      name:         prev.name || a.organisation || prev.name,
                      addressLine1: a.addressLine1,
                      addressLine2: a.addressLine2,
                      city:         a.city,
                      county:       a.county,
                      postcode:     a.postcode,
                    }))}
                  />
                  <p style={{ margin: 0, fontSize: 12, color: '#0369a1' }}>Or enter the address manually below</p>
                </div>
              )}

              <div className="form-group">
                <label>Address Line 1 *</label>
                <input
                  type="text"
                  required
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  placeholder="Street address"
                />
              </div>

              <div className="form-group">
                <label>Address Line 2</label>
                <input
                  type="text"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  placeholder="Apartment, suite, unit, etc. (optional)"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City or town"
                  />
                </div>

                <div className="form-group">
                  <label>County</label>
                  <input
                    type="text"
                    value={formData.county}
                    onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                    placeholder="County (optional)"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Postcode *</label>
                <input
                  type="text"
                  required
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="e.g. M1 1AA"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional details about this site"
                  rows={3}
                  style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSite ? 'Update' : 'Create'} Site
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        {sites.length === 0 ? (
          <p className="empty-state">No sites found. Create your first site to organize your vehicles by location.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Site Name</th>
                <th>Address</th>
                <th>Description</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.id}>
                  <td><strong>{site.name}</strong></td>
                  <td>
                    {site.addressLine1}{site.addressLine2 ? `, ${site.addressLine2}` : ''}, {site.city}, {site.postcode}
                  </td>
                  <td>{site.description}</td>
                  <td>{formatDateBritish(site.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-icon" 
                        onClick={() => handleEdit(site)}
                        title="Edit site"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleDelete(site.id)}
                        title="Delete site"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SiteList;
